import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { z } from 'zod'

import { DateRange } from '@/services/create-pulverization'
import { DisplayValue } from '@/types/weed-diagnosis'
import { makeGenericZodErrorMessage } from '@/utils/make-generic-zod-error-message'

const dateSchema = z.coerce
  .date(makeGenericZodErrorMessage('A data é obrigatória.'))
  .min(new Date('1900-01-01'), { message: 'Data muito antiga.' })
  .transform((value) => value.toJSON().split('T')[0])

const equipmentDefinitionFormSchema = z.object({
  layersMap: z.record(z.unknown()).optional(),
  mergedLayersMap: z.record(z.unknown()).optional(),
  organization: z.string().optional(),
  pulverization_date: z
    .object({
      start_date: dateSchema,
      end_date: z.union([dateSchema, z.string()]),
    })
    .refine(
      (data) => {
        if (!data.end_date) return true
        return data.start_date <= data.end_date
      },
      {
        message: 'Data final deve ser maior que a data inicial.',
        path: ['end_date'],
      },
    ),
})

type EquipmentDefinitionFormSchema = z.infer<
  typeof equipmentDefinitionFormSchema
>

type EquipmentValue = Record<string, DisplayValue>

export interface EquipmentFormValue extends EquipmentDefinitionFormSchema {
  layersMap: EquipmentValue
  mergedLayersMap: EquipmentValue
  organization?: string
  pulverization_date: DateRange
}

export function EquipmentDefinitionFormProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const methods = useForm<EquipmentFormValue>({
    reValidateMode: 'onSubmit',
    shouldUseNativeValidation: true,
    resolver: zodResolver(equipmentDefinitionFormSchema),
  })

  return <FormProvider {...methods}>{children}</FormProvider>
}

export function useEquipmentDefinitionForm() {
  return useFormContext<EquipmentFormValue>()
}
